import { useState } from 'react'
import { api } from '../lib/api'
import { setCachedUser } from '../lib/auth'
import type { User } from '../lib/types'

interface Props {
  user: User
  onUpdate: (user: User) => void
}

export default function Profile({ user, onUpdate }: Props) {
  const [name, setName] = useState(user.name || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showErase, setShowErase] = useState(false)
  const [eraseConfirm, setEraseConfirm] = useState('')
  const [eraseMsg, setEraseMsg] = useState('')
  const [erasing, setErasing] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    try {
      const updated = await api.updateProfile(name)
      setCachedUser(updated)
      onUpdate(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <h1 className="text-2xl font-bold mb-6">Your Profile</h1>

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <p className="text-gray-900">{user.email}</p>
        </div>

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Display Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Your name"
            className="input"
          />
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saved && <span className="text-green-600 text-sm">Saved!</span>}
        </div>
      </form>

      <div className="card mt-6 p-4">
        <h2 className="font-semibold mb-2">About This App</h2>
        <p className="text-sm text-gray-600">
          Automates 2026 Conference Companion helps you prepare for and navigate
          the Automates trade show at McCormick Place, Chicago. Browse exhibitors,
          prepare targeted questions, take notes at booths, and record vendor
          conversations for later review.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Your data is private and only visible to you.
        </p>
      </div>

      {/* Privacy & Data (GDPR self-service, SPEC-DATA) */}
      <div className="card mt-6 p-4">
        <h2 className="font-semibold mb-1">Privacy &amp; Data</h2>
        <p className="text-sm text-gray-500 mb-3">
          Export or erase everything, instantly. <a href="/privacy" className="text-brand-600 underline">Privacy policy</a>
        </p>
        <div className="flex gap-3 flex-wrap">
          <a href={api.privacyExportUrl()} download className="btn-secondary text-sm">⬇ Download my data (JSON)</a>
          <button onClick={() => setShowErase(true)}
            className="text-sm px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50">
            Erase my account…
          </button>
        </div>
      </div>

      {showErase && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={() => setShowErase(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-red-700 mb-2">Erase everything, forever</h3>
            <p className="text-xs text-gray-600 mb-4">
              Destroys your account, booth notes, question answers, recordings (including audio files),
              and track progress. Security logs are pseudonymized. This cannot be undone.
              Type <b>{user.email}</b> to confirm.
            </p>
            <input value={eraseConfirm} onChange={e => setEraseConfirm(e.target.value)} placeholder={user.email}
              className="input mb-4" />
            {eraseMsg && <p className="text-xs text-red-600 mb-3">{eraseMsg}</p>}
            <div className="flex gap-3">
              <button
                disabled={eraseConfirm.trim().toLowerCase() !== user.email.toLowerCase() || erasing}
                onClick={async () => {
                  setErasing(true)
                  try {
                    await api.privacyErase(eraseConfirm.trim())
                    const { clearAuth } = await import('../lib/auth')
                    clearAuth()
                    window.location.href = '/'
                  } catch (err) {
                    setEraseMsg(err instanceof Error ? err.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Erasure failed')
                    setErasing(false)
                  }
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium disabled:opacity-40">
                {erasing ? 'Erasing…' : 'Erase my account'}
              </button>
              <button onClick={() => setShowErase(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
